// components/AIActDisclosureModal.tsx — one-time disclosure shown to the
// student that AI Tutor is an AI-driven tutor (Il Prof is an AI assistant,
// not a human teacher) and that interactions are saved to personalize the
// study path. Anchors the EU AI Act Art 13 transparency obligation
// (pivot-design.md §6.3 row "AI processing disclosure"). The full §6 consent
// capture flow is W8 territory; this modal is the M1-socio-ready slice.
//
// Sequencing: fires on the first authenticated render AFTER the onboarding
// tour has already played, mounted alongside the main student app shell so the
// invite-code → tour chain finishes first. We pick the cleanest existing
// primitive: this component reads the persistent flag on mount and renders
// nothing if already acknowledged. Mounting it inside `AuthGate` after the
// tour completes guarantees the tour key is set first.
//
// Persistence: localStorage `asc_ai_disclosure_v1`. Pure client-side per
// scope — no BE endpoint. The W8 consent capture flow will introduce a
// server-side `consent_records` row with policy_version + timestamp and
// deprecate this localStorage gate; until then a single bit is enough for
// the socio-demo posture.
//
// Accessibility:
//  - role="dialog" + aria-modal="true" + aria-labelledby on the heading.
//  - "Capito" button receives focus on mount; previously-focused element
//    refocused on dismiss.
//  - Esc key dismisses with the same persistence semantics as Capito.
//  - Tab focus-trap inside the modal so keyboard focus can't leak to the
//    underlying page.
//  - 44px minimum touch target on the CTA (mobile a11y).
//
// Italian "tu" voice — Prof tone — describes:
//   1. AI is in use ("ti aiuta a studiare ... usando l'intelligenza artificiale").
//   2. Il Prof is AI not human ("Il Prof è un assistente AI, non un insegnante umano").
//   3. Interactions are saved to personalize the path ("le tue domande,
//      risposte e progressi vengono salvati per personalizzare il tuo
//      percorso di studio").

import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTokens } from '../state/theme';

/**
 * Single source of truth for the localStorage key. Exported so tests + any
 * future migration helper reference the same string. Bumping the suffix
 * (`_v2`) on copy or scope change re-prompts every existing student.
 */
export const AI_DISCLOSURE_LS_KEY = 'asc_ai_disclosure_v1';
export const AI_DISCLOSURE_ACK_EVENT = 'asc_ai_disclosure_seen';

/** Read-only check used by the parent shell to decide whether to mount. */
export function hasSeenAIDisclosure(): boolean {
  try {
    return localStorage.getItem(AI_DISCLOSURE_LS_KEY) === '1';
  } catch {
    // Storage disabled / quota error — fail closed (don't badger the student
    // every page load if we can't persist).
    return true;
  }
}

function markAIDisclosureSeen(): void {
  try {
    localStorage.setItem(AI_DISCLOSURE_LS_KEY, '1');
  } catch {
    /* noop — quota or disabled storage; non-fatal */
  }
}

function notifyAIDisclosureAcknowledged(): void {
  try {
    window.dispatchEvent(new Event(AI_DISCLOSURE_ACK_EVENT));
  } catch {
    /* noop — non-browser edge; localStorage remains the source of truth */
  }
}

interface Props {
  /**
   * Optional callback fired after the student dismisses the modal (Capito or
   * Esc). The parent shell can rerender to remove the modal from the tree.
   */
  onDismiss?: () => void;
}

export function AIActDisclosureModal({ onDismiss }: Props): JSX.Element | null {
  const tokens = useTokens();
  // Initial visibility: render iff the flag is unset. We capture this once
  // on first render so the modal doesn't disappear mid-transition if another
  // tab writes the key.
  const [visible, setVisible] = useState<boolean>(() => !hasSeenAIDisclosure());

  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const ctaRef = useRef<HTMLButtonElement | null>(null);
  const headingId = 'ai-act-disclosure-title';

  function dismiss(): void {
    markAIDisclosureSeen();
    notifyAIDisclosureAcknowledged();
    setVisible(false);
    onDismiss?.();
  }

  // Mount-time effects: capture previously-focused element, focus the CTA,
  // wire up Esc + Tab focus-trap, restore focus on unmount.
  useEffect(() => {
    if (!visible) return;
    previouslyFocusedRef.current = (document.activeElement as HTMLElement) ?? null;
    // Focus the primary CTA so screen-reader users land on the actionable
    // affordance immediately.
    ctaRef.current?.focus();

    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        e.preventDefault();
        dismiss();
        return;
      }
      if (e.key === 'Tab') {
        // Single-CTA modal: keep focus on the only focusable child. Cycling
        // is trivially Tab → Tab → Tab still on the same button, but we
        // intercept to prevent the browser from escaping to the underlying
        // page (where the home grid and drawer would steal focus).
        const node = containerRef.current;
        if (!node) return;
        const focusable = Array.from(
          node.querySelectorAll<HTMLElement>(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
          ),
        ).filter((el) => !el.hasAttribute('disabled'));
        if (focusable.length === 0) {
          e.preventDefault();
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (e.shiftKey) {
          if (active === first || !node.contains(active)) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (active === last || !node.contains(active)) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    }

    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      // Restore focus to whatever held it before the modal opened.
      previouslyFocusedRef.current?.focus?.();
    };
    // The handler closes over `dismiss` which is stable enough for our
    // purposes — re-binding on every render is fine because the listener
    // is registered/removed in lockstep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      data-testid="ai-disclosure-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby={headingId}
      style={{
        position:       'fixed',
        inset:          0,
        background:     'rgba(0,0,0,0.55)',
        display:        'flex',
        alignItems:     'center',
        justifyContent: 'center',
        padding:        '20px',
        zIndex:         1000,
      }}
      // Backdrop click intentionally does NOT dismiss — the student must
      // explicitly acknowledge via Capito or Esc so the AI Act Art 13
      // notification can be evidenced ("user took an action").
    >
      <div
        ref={containerRef}
        tabIndex={-1}
        style={{
          background:    tokens.bg,
          color:         tokens.text,
          border:        `1px solid ${tokens.line}`,
          borderRadius:  tokens.radius.lg,
          padding:       '28px 24px 24px',
          maxWidth:      460,
          width:         '100%',
          outline:       'none',
          boxShadow:     '0 10px 40px rgba(0,0,0,0.35)',
        }}
      >
        <h2
          id={headingId}
          style={{
            fontFamily:    tokens.fonts.serif,
            fontSize:      22,
            fontWeight:    700,
            color:         tokens.text,
            margin:        '0 0 14px 0',
            letterSpacing: -0.3,
            lineHeight:    1.25,
          }}
        >
          Ciao! Una cosa importante prima di iniziare
        </h2>

        <p
          data-testid="ai-disclosure-body"
          style={{
            fontFamily:  tokens.fonts.body,
            fontSize:    15,
            color:       tokens.textMuted,
            margin:      '0 0 20px 0',
            lineHeight:  1.6,
          }}
        >
          AI Tutor ti aiuta a studiare la Patente B usando l&rsquo;intelligenza artificiale.
          {' '}
          Il Prof è un assistente AI, non un insegnante umano. Le tue domande, risposte e
          progressi vengono salvati per personalizzare il tuo percorso di studio. Buono studio!
        </p>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            ref={ctaRef}
            data-testid="ai-disclosure-cta"
            onClick={dismiss}
            style={{
              background:    tokens.accent,
              // Light-only (V28 F1): tokens.mode always resolves 'light'.
              color:         '#fff',
              border:        'none',
              borderRadius:  tokens.radius.pill,
              padding:       '12px 28px',
              cursor:        'pointer',
              fontFamily:    tokens.fonts.body,
              fontSize:      15,
              fontWeight:    700,
              minHeight:     44,
              minWidth:      120,
              letterSpacing: -0.1,
            }}
          >
            Capito
          </button>
        </div>
      </div>
    </div>
  );
}
